"use client"

import { BurgerMenuMobile, CloseIcon } from "@local/ui/src/components/ui/icons"
import { useMenuLayout } from "../../../../lib/useMenuLayout"
import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"
import { cn } from "@local/utils/src/cn"
import { usePathname, useParams } from "next/navigation"
import { getT } from "@local/i18n/src/t"
import {
  getLanguageForMarket,
  localizeMarketPath,
  marketBaseOf,
} from "@local/i18n/src/languages"
import { a11y } from "@local/i18n/src/catalogs/a11y"
import type { NavigationMainItem } from "../types"
import type { Route } from "next"
import { Link } from "@local/ui/src/components/ui/Link"
import { Modal } from "@local/ui/src/components/ui/Modal"
import dynamic from "next/dynamic"

// Dynamically import static page components for modal display
const AboutUs = dynamic(
  () => import("../../StaticPages/AboutUs").then((mod) => mod.AboutUs),
  { ssr: false }
)

const VeteransCharities = dynamic(
  () =>
    import("../../StaticPages/VeteransCharities").then(
      (mod) => mod.VeteransCharities
    ),
  { ssr: false }
)

const BaerskinStories = dynamic(
  () =>
    import("../../StaticPages/BaerskinStories").then(
      (mod) => mod.BaerskinStories
    ),
  { ssr: false }
)

const PrivacyPolicy = dynamic(
  () =>
    import("../../StaticPages/PrivacyPolicy").then((mod) => mod.PrivacyPolicy),
  { ssr: false }
)

const TermsConditions = dynamic(
  () =>
    import("../../StaticPages/TermsConditions").then(
      (mod) => mod.TermsConditions
    ),
  { ssr: false }
)

const ShippingReturnsRefunds = dynamic(
  () =>
    import("../../StaticPages/ShippingReturnsRefunds").then(
      (mod) => mod.ShippingReturnsRefunds
    ),
  { ssr: false }
)

const SecurePayments = dynamic(
  () =>
    import("../../StaticPages/SecurePayments").then(
      (mod) => mod.SecurePayments
    ),
  { ssr: false }
)

const Accessibility = dynamic(
  () =>
    import("../../StaticPages/Accessibility").then((mod) => mod.Accessibility),
  { ssr: false }
)

const MOBILE_NAV_DRAWER_ID = "mobile-nav-drawer"

interface Props {
  market: string
  navigationMain?: NavigationMainItem[]
}

interface MenuItemProps {
  item: NavigationMainItem
  setOpen: (target?: boolean | undefined) => void
  market: string
}

interface SubMenuItemProps {
  item: NavigationMainItem
  setOpen: (target?: boolean | undefined) => void
  index: number
  market: string
}

/**
 * Determines if a URL should open in a modal instead of navigating
 */
const shouldOpenModal = (url?: string) => {
  if (!url) return false
  return (
    url.includes("about-us") ||
    url.includes("our-story") ||
    url.includes("privacy-policy") ||
    url.includes("terms-and-conditions") ||
    url.includes("shipping-returns-and-refunds") ||
    url.includes("secure-payments") ||
    url.includes("veterans-charities") ||
    url.includes("baerskin-stories") ||
    url.includes("accessibility")
  )
}

/**
 * Gets the appropriate modal content based on URL
 */
const getModalContent = (market: string, url?: string) => {
  if (!url) return null

  switch (true) {
    case url.includes("about-us") || url.includes("our-story"):
      return <AboutUs market={market} />
    case url.includes("privacy-policy"):
      return <PrivacyPolicy market={market} />
    case url.includes("terms-and-conditions"):
      return <TermsConditions market={market} />
    case url.includes("shipping-returns-and-refunds"):
      return <ShippingReturnsRefunds market={market} />
    case url.includes("secure-payments"):
      return <SecurePayments market={market} />
    case url.includes("veterans-charities"):
      return <VeteransCharities market={market} />
    case url.includes("baerskin-stories"):
      return <BaerskinStories market={market} />
    case url.includes("accessibility"):
      return <Accessibility market={market} />
    default:
      return null
  }
}

export const HeaderMenuMobileButton = () => {
  const { toggleOpen: toggleMenu, isOpen: open } = useMenuLayout()
  const pathname = usePathname()
  const params = useParams<{ market?: string }>()
  const t = getT(getLanguageForMarket(params?.market), { a11y })

  useEffect(() => {
    toggleMenu(false)
  }, [pathname, toggleMenu])

  return (
    <button
      type="button"
      className="-ml-1 aspect-square h-full p-1 sm:ml-1 xl:hidden"
      onClick={() => toggleMenu(!open)}
      aria-expanded={open}
      aria-controls={MOBILE_NAV_DRAWER_ID}
      aria-label={open ? t("a11y.closeMenu") : t("a11y.openMenu")}
    >
      <div
        className="flex justify-center text-sm md:text-xs"
        aria-hidden="true"
      >
        <BurgerMenuMobile />
      </div>
    </button>
  )
}

const MenuSubItemMobile = ({
  item,
  index,
  market,
  setOpen,
}: SubMenuItemProps) => {
  const [modalOpen, setModalOpen] = useState(false)
  const modalContent = getModalContent(market, item.url)

  if (!item.subitems || item.subitems.length === 0) {
    // If this link should open a modal
    if (shouldOpenModal(item.url)) {
      return (
        <>
          <button
            id={`mobile-featured-heading-${item.id}-${index}`}
            className="flex flex-row justify-between font-medium text-gray-100 hover:opacity-75"
            onClick={() => {
              setModalOpen(true)
              // Keep menu open when opening modal
            }}
            type="button"
          >
            {item.name}
          </button>
          <Modal
            open={modalOpen}
            toggle={setModalOpen}
            contentClassName="max-w-4xl"
          >
            {modalContent}
          </Modal>
        </>
      )
    }

    // Regular link behavior - closes menu on navigation
    return (
      <Link
        id={`mobile-featured-heading-${item.id}-${index}`}
        className="flex flex-row justify-between font-medium text-gray-100 hover:opacity-75"
        href={
          (item.anchor
            ? `#${item.anchor}`
            : ((item.url && localizeMarketPath(item.url, market)) ??
              "#")) as Route
        }
        prefetch={false}
        onClick={() => setOpen(false)}
      >
        {item.name}
      </Link>
    )
  }

  if (!item.url) {
    return null
  }

  return (
    <Fragment>
      <Link
        href={localizeMarketPath(item.url, market) as Route}
        id={`mobile-featured-heading-${item.id}-${index}`}
        className={
          "flex flex-row justify-between font-medium text-gray-100 hover:opacity-75"
        }
        prefetch={false}
      >
        <span id={`mobile-list-heading-${item.id}`}>{item.name}</span>
      </Link>
      <ul
        aria-labelledby={`mobile-list-heading-${item.id}`}
        className="space-y-2"
      >
        {item.subitems?.map((subitem, i) => (
          <li key={`menu-item-mobile-${subitem.id}-${i}`} className="flex">
            {subitem.url && (
              <Link
                id={`menu-item-mobile-client-${item.id}-${i}`}
                href={localizeMarketPath(subitem.url, market) as Route}
                className="text-gray-400"
                prefetch={false}
              >
                {subitem.name}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Fragment>
  )
}

const MenuItemMobile = ({ item, setOpen, market }: MenuItemProps) => {
  return (
    <div className="space-y-4 px-4 pt-10">
      <div className="grid grid-cols-1 items-start gap-x-4 gap-y-4">
        <div className="grid grid-cols-1 gap-4">
          <MenuSubItemMobile
            index={0}
            item={item}
            market={market}
            setOpen={setOpen}
          />
        </div>
      </div>
    </div>
  )
}

export const HeaderMenuMobile = ({ market, navigationMain }: Props) => {
  const { toggleOpen: toggleMenu, isOpen: open } = useMenuLayout()
  const t = getT(getLanguageForMarket(market), { a11y })
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  // DEV-9850: `market` may be a public language-variant slug ("de-en") — the
  // "Kids" nav item is a US-only content rule (market identity), not language.
  const filteredNavigation =
    marketBaseOf(market) !== "us"
      ? navigationMain?.filter((item) => item.name !== "Kids")
      : navigationMain

  useEffect(() => {
    if (!open) return

    previousFocusRef.current = document.activeElement as HTMLElement | null
    requestAnimationFrame(() => {
      closeButtonRef.current?.focus()
    })

    return () => {
      if (previousFocusRef.current) {
        previousFocusRef.current.focus()
        previousFocusRef.current = null
      }
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        toggleMenu(false)
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("keydown", handleEscape)
    }
  }, [open, toggleMenu])

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !dialogRef.current) return

    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }, [])

  return (
    <div
      className={cn(
        "relative z-240 xl:hidden",
        open ? "opacity-100" : "opacity-0"
      )}
      style={{
        transition: "opacity linear 300ms",
      }}
      inert={!open ? true : undefined}
    >
      <div
        className={cn(
          "fixed inset-0 z-50 flex",
          open ? "visible block" : "invisible hidden",
          "w-full bg-gray-600/50 backdrop-blur-xs"
        )}
        role="presentation"
        onClick={() => toggleMenu(false)}
      />
      <div
        className={cn(
          "fixed inset-0 z-50 flex",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        style={{
          transition: "transform ease-in-out 300ms",
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog role with onKeyDown for Tab trap is the standard modal a11y pattern */}
        <div
          ref={dialogRef}
          id={MOBILE_NAV_DRAWER_ID}
          role="dialog"
          aria-modal="true"
          aria-label={t("a11y.mainMenu")}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          className="absolute left-0 flex h-full w-full max-w-xs flex-col overflow-y-auto bg-gray-950 pb-12 text-white shadow-xl md:max-w-sm lg:max-w-md"
        >
          <div className="flex px-4 pt-5 pb-2">
            <button
              ref={closeButtonRef}
              type="button"
              aria-label={t("a11y.closeMenu")}
              className={cn(
                "-m-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400"
              )}
              onClick={() => toggleMenu(false)}
            >
              <span className="sr-only">{t("a11y.closeMenu")}</span>
              <CloseIcon aria-hidden="true" />
            </button>
          </div>

          {/* Links */}
          <nav className="mt-2" aria-label={t("a11y.mobileMenu")}>
            {filteredNavigation?.map((item, i) => (
              <MenuItemMobile
                key={`menu-mobile-${i}`}
                item={item}
                setOpen={toggleMenu}
                market={market}
              />
            ))}
          </nav>
        </div>
        <div
          className="h-full w-full"
          role="presentation"
          onClick={() => toggleMenu(false)}
        />
      </div>
    </div>
  )
}
